import { Injectable, inject, signal } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { IUiAffordabilityModel, StepName } from '@clubhub/ignite/models/affordability';
import { ResultsView } from '@clubhub/ignite/models/affordability/enums';
import { IUiPropertySearchModel, PropertyAuditResultsStatus, PropertyModelRequest } from '@clubhub/ignite/models/property';
import { ProductsSearchService } from '@clubhub/ignite/services';
import { FormlyFormOptions } from '@ngx-formly/core';
import { SimpleGridComponent } from '@shared/components/simple-grid';
import { roles } from '@shared/constants/roles';
import { FindAddressResponse, LendingType, OutcomeDetailsCard, OutcomeResponse, Product, PropertyOutcomeDetailsDetailed } from '@shared/models';
import { SimpleGridResponseViewModel } from '@shared/models/simple-grid';
import { AddressLookupService, AuthorizeService, ClubHubDataService, LendingTypeService, ModalService } from '@shared/services';
import { firstValueFrom } from 'rxjs';
import { AffordabilityService } from '../affordability/affordability.service';
import { CriteriaV2Service } from '../criteria/criteria-v2.service';
import { IgniteCommonDataService } from '../ignite.store';
import { S365Service } from '../s365.service';
import { SavedSearchCommonDataService } from '../saved-searches/saved-search-common-data.service';

const requiredPropertyFields = [
  'propertyValue',
  'loanAmount',
  'postcode',
  'location',
  'propertyType',
  'numberOfBedrooms',
];

@Injectable({
  providedIn: 'root',
})
export class PropertySearchService {
  private readonly criteriaV2Service = inject(CriteriaV2Service);
  private readonly affordabilityService = inject(AffordabilityService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);
  private readonly productsSearchService = inject(ProductsSearchService);
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly dataService = inject(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);
  private readonly modalService = inject(ModalService);
  private readonly s365Service = inject(S365Service);
  private readonly addressLookupService = inject(AddressLookupService);
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);

  public propertyLoading = true;
  public form = new UntypedFormGroup({});
  public model: IUiPropertySearchModel | undefined;
  public options = {} as FormlyFormOptions;
  private readonly _currentStepName = signal<StepName | undefined>(undefined);
  public matchedLenders: string[] = [];
  public propertyModelRequest: PropertyModelRequest | null = null;
  public allFilteredProperty: SimpleGridResponseViewModel<Product> | null = null;
  public allPropertyDiscreteTypes: string[] = [];
  public allPropertyDiscreteInitialPeriods: number[] = [];
  public allLenders: string[] = [];
  public auditResultsStatus: PropertyAuditResultsStatus = PropertyAuditResultsStatus.NotSubmitted;
  public propertyGrid: SimpleGridComponent | null = null;
  public resultsView: ResultsView = ResultsView.Details;
  public auditResultsCurrentStatus: PropertyAuditResultsStatus | null = null;
  public propertySearchId: string | null = null;
  public isAuditHistory: boolean = false;
  public requestHistory: unknown;
  public lenderList: string[] = [];
  public addresses: FindAddressResponse[] = [];
  public showAddress = false;
  public postcodeLookup = true;
  public hadValuesOriginally = false;
  public autoAddedPropertyOutcomeIds: number[] = [];
  public propertySearchRequestViewModel: PropertyModelRequest | null = null;
  public preservePropertyCriteria = false;
  public hasPresetPropertyValue: boolean = false;
  public propertyOutcomeIds: number[] = [];
  public propertyOutcomes: OutcomeResponse[] = [];

  public get currentStepName(): StepName | undefined {
    return this._currentStepName();
  }

  public set currentStepName(value: StepName | undefined) {
    this._currentStepName.set(value);
  }

  public constructor() {
    this.reset();
  }

  public reset() {
    (this.form.controls.propertyDetails as UntypedFormGroup)?.markAsUntouched();
    this.form = new UntypedFormGroup({});
    this.model = this.initialModel();
    this.currentStepName = StepName.MortgageRequirements;
    if (this.options.resetModel) {
      this.options.resetModel();
    }
    this.options = {};
    this.addresses = [];
    this.showAddress = false;
    this.postcodeLookup = true;
    this.igniteCommonDataService.propertyModel = this.model;
  }

  public resetParamsToDefault(): void {
    this.igniteCommonDataService.propertyModel = null;
    this.propertySearchId = null;
    this.propertyLoading = true;
    this.matchedLenders = [];
    this.propertyModelRequest = null;
    this.allFilteredProperty = null;
    this.allPropertyDiscreteTypes = [];
    this.allPropertyDiscreteInitialPeriods = [];
    this.allLenders = [];
    this.auditResultsStatus = PropertyAuditResultsStatus.NotSubmitted;
    this.propertyGrid = null;
    this.resultsView = ResultsView.Details;
    this.auditResultsCurrentStatus = null;
    this.isAuditHistory = false;
    this.requestHistory = null;
    this.lenderList = [];
    this.addresses = [];
    this.showAddress = false;
    this.postcodeLookup = true;
    this.hadValuesOriginally = false;
  }

  public getPropertyFormValue() {
    return this.form.value;
  }

  private get actualLendingType(): LendingType | undefined {
    if (this.productsSearchService.isSecondChargeProducts) {
      return this.lendingTypeService.getLendingTypeByCode('2nd');
    }

    return this.lendingTypeService.lendingType;
  }

  private get productTypeCode(): string | undefined {
    return this.actualLendingType?.code;
  }

  public get autoAddedProperty(): boolean {
    return this.autoAddedPropertyOutcomeIds.length > 0;
  }

  public get isLender() {
    return this.authService.hasRole(roles.lender);
  }

  public get loading() {
    return this.affordabilityService.loading;
  }

  private initialModel(): IUiPropertySearchModel {
    this.hadValuesOriginally = false;
    const productModel = this.igniteCommonDataService.productModel;
    if (this.igniteCommonDataService.productsOnly && productModel) {
      const propertyValue = productModel.mortgageRequirements?.propertyValue ?? null;
      const loanAmount = productModel.mortgageRequirements?.loanAmount ?? null;

      this.hadValuesOriginally = !!propertyValue && !!loanAmount;
      return {
        propertyDetails: {
          propertyValue,
          loanAmount,
        },
      } as IUiPropertySearchModel;
    }

    return {} as IUiPropertySearchModel;
  }

  public async getProperty(igniteModel?: IUiAffordabilityModel) {
    this.criteriaV2Service.loading = true;
    this.propertyModelRequest = await this.mappedModel(
      igniteModel,
      (this.form.valid && Object.keys(this.form.value).length > 0) ? this.form.value : this.model,
    );
    const propertySearchRequestViewModel = this.propertyModelRequest;
    this.propertySearchRequestViewModel = propertySearchRequestViewModel;
    this.resultsView = ResultsView.Details;
    this.currentStepName = StepName.Results;
    setTimeout(() => {
      this.criteriaV2Service.loading = false;
    }, 15000); // if you change the length of this timeout you will need to change it in the css animation too.

    // Clear property criteria from basket before getting new one
    this.criteriaV2Service.clearBasketProperty();
    // Clear EPC Rating intuitive criteria from basket before getting new one,
    // as if a property has no rating it would cause the previous rating to still show
    this.criteriaV2Service.removeBasketItemByContextName('EPC Rating');

    this.dataService.post<PropertyOutcomeDetailsDetailed>('propertyCriteriaV2', propertySearchRequestViewModel).subscribe((result) => {
      this.affordabilityService.loading = false;
      this.criteriaV2Service.loading = false;
      this.propertySearchId = result.propertySearchId;
      this.autoAddedPropertyOutcomeIds = result.autoAddedOutcomeIds ?? [];
      this.setPropertyResults(result, this.propertySearchId, !this.igniteCommonDataService.propertyOnly);
      this.igniteCommonDataService.resultsView = ResultsView.Summary;
      this.igniteCommonDataService.lenders = result.details.map(x => ({
        lenderName: x.overallResult.lender,
        lenderLogoUrl: x.overallResult.lenderLogoUrl,
      }));
      this.igniteCommonDataService.searchToolsInUse.property = true;
    });

    this.savedSearchCommonDataService.clearSavedSearch();

    this.modalService.closeAll();
  }

  public setPropertyResults(outcomes: PropertyOutcomeDetailsDetailed, propertySearchId: string, addingProperty = false) {
    this.propertySearchId = propertySearchId;
    const outcomeDetails = this.criteriaV2Service.outcome;
    let isFirstTimePropertyCheck = true;
    if (!outcomeDetails.details || !outcomeDetails.contexts) {
      // First time Property check
      outcomeDetails.details = [];
      outcomeDetails.contexts = [];
    } else {
      isFirstTimePropertyCheck = false;
      // When the client Change the property to a new address or add Property check from affordability we need to remove
      // all Added Automatically criteria so that we can add the new Property's criteria
      outcomeDetails.details.forEach(x => {
        x.contextResults = x.contextResults.filter(y => !(y.isProperty && y.autoAdded));
      });
      outcomeDetails.contexts = outcomeDetails.contexts?.filter(x =>
        outcomeDetails.details.find(y => y.contextResults.find(z => z.outcomeId === x.answerId)),
      );
    }

    outcomes.details.forEach(x => {
      const lenderOutcome = outcomeDetails.details.filter(y => y.overallResult.lender === x.overallResult.lender);
      if (lenderOutcome.length) {
        const lenderDetails = lenderOutcome[0];
        lenderDetails.contextResults = lenderDetails.contextResults.concat(x.contextResults);
        // Get overall outcome Result
        const lenderOutcomeResult = lenderDetails.overallResult.outcomeResult;
        const propertySearchOutcomeResult = x.overallResult.outcomeResult;
        const overallOutcomeResult = this.criteriaV2Service.getOverallOutcome(lenderOutcomeResult, propertySearchOutcomeResult);
        if (overallOutcomeResult) {
          lenderDetails.overallResult.outcomeResult = overallOutcomeResult.toString();
        }
      } else {
        outcomeDetails.details.push(x);
      }
    });

    const mappedContexts = outcomes.contexts.map(oc => {
      return {
        answerId: oc.answerId,
        context: oc.context?.text,
        issue: `${oc.issue?.text} - Added Automatically`,
        questionsAndAnswers: oc.questionsAndAnswers.map(ocq => {
          return {
            question: ocq.question.text,
            answer: ocq.answer.text,
          };
        }),
      };
    });

    const conceitedContexts = outcomeDetails.contexts?.concat(mappedContexts);

    outcomeDetails.contexts = [...conceitedContexts, ...this.criteriaV2Service.intuitiveContextCards];
    this.splitOutPropertyCriteria(outcomes.intuitiveDetails, outcomeDetails.details, addingProperty);

    this.criteriaV2Service.updateBasketItems(outcomes.contexts as unknown as OutcomeDetailsCard[], isFirstTimePropertyCheck);
  }

  public async getPropertyIfRequired(igniteModel?: IUiAffordabilityModel) {
    const hasProperty =
      // Default form value is empty object rather than null, so check number of keys in object too
      (this.form.value && Object.keys(this.form.value).length && this.form.valid) ||
      // Model gets partially patched when doing other forms (e.g. product will set the propertyValue and loanAmount), so check all
      // mandatory fields are present.
      (this.model?.propertyDetails && requiredPropertyFields.every(k => k in (this.model?.propertyDetails || {})));

    if (hasProperty) {
      return this.getProperty(igniteModel);
    }
  }

  private getPropertyOutcomes(criteriaOutcomes: OutcomeResponse[]): OutcomeResponse[] {
    return criteriaOutcomes.map(outcome => ({
      contextResults: outcome.contextResults.filter(context => context.isProperty),
      overallResult: outcome.overallResult,
      skipList: outcome.skipList,
    }));
  }

  private markAutoAddedPropertyOutcomes(propertyOutcomes: OutcomeResponse[]): void {
    this.autoAddedPropertyOutcomeIds.forEach(autoAddedId => {
      propertyOutcomes.forEach(outcome => {
        outcome.contextResults.every(context => {
          if (context.contextId === autoAddedId) {
            context.autoAdded = true;
            return false;
          }
          return true;
        });
      });
    });
  }

  public splitOutPropertyCriteria(
    intuitiveOutcomes: OutcomeResponse[] | null,
    criteriaOutcomes: OutcomeResponse[],
    addingProperty = false,
  ) {
    if (intuitiveOutcomes?.length) {
      this.criteriaV2Service.intuitiveOutcomes = this.criteriaV2Service.getNonPropertyOutcomes(intuitiveOutcomes);
    }

    if (!addingProperty) {
      this.criteriaV2Service.additionalOutcomes = this.criteriaV2Service.getAdditionalOutcomes(criteriaOutcomes);
    }

    const propertyOutcomes = this.getPropertyOutcomes(criteriaOutcomes);
    this.markAutoAddedPropertyOutcomes(propertyOutcomes);

    this.criteriaV2Service.additionalOutcomeIds = this.criteriaV2Service.getUniqueOutcomeIds(this.criteriaV2Service.additionalOutcomes);
    this.propertyOutcomeIds = this.criteriaV2Service.getUniqueOutcomeIds(propertyOutcomes);

    this.propertyOutcomes = propertyOutcomes;
  }

  public get isDetailsView() {
    return this.resultsView === ResultsView.Details;
  }

  public get isInputView() {
    return this.resultsView === ResultsView.Input;
  }

  public updateModel(model = {}) {
    this.model = { ...this.model, ...model } as unknown as IUiPropertySearchModel;
    this.igniteCommonDataService.propertyModel = this.model;
  }

  private async mappedModel(igniteModel: IUiAffordabilityModel | undefined, uiModel: IUiPropertySearchModel) {
    const { propertyDetails } = uiModel;

    let address = propertyDetails.manualAddress;

    // If the user selected an address from a lookup, we need to resolve that address ID into a full address
    if (propertyDetails.addressId) {
      const resolvedAddress = await firstValueFrom(this.addressLookupService.retrieveAddress(propertyDetails.addressId));
      address = resolvedAddress.fullAddress;
      delete propertyDetails.addressId;
    }

    if (this.igniteCommonDataService.affordabilityOnly) {
      propertyDetails.loanAmount = igniteModel?.propertyAndLoan?.loanAmount;
      propertyDetails.propertyValue = igniteModel?.propertyAndLoan?.propertyValue;
      propertyDetails.location = igniteModel?.propertyAndLoan?.location;
      propertyDetails.propertyType = igniteModel?.propertyAndLoan?.propertyType;
      propertyDetails.newBuild = igniteModel?.propertyAndLoan?.newBuild;
    }

    if (this.igniteCommonDataService.productsOnly) {
      const productModel = this.igniteCommonDataService.productModel;
      propertyDetails.loanAmount = productModel?.mortgageRequirements?.loanAmount;
      propertyDetails.propertyValue = productModel?.mortgageRequirements?.propertyValue;
    }

    return {
      ...propertyDetails,
      address,
      propertySearchId: this.propertySearchId,
      caseId: this.s365Service.s365Data?.caseId ?? null,
      includeIntuitive: this.igniteCommonDataService.propertyOnly,
      productType: this.productTypeCode,
      affordabilitySearchId: this.igniteCommonDataService.propertyOnly ? null : this.igniteCommonDataService.affordabilitySearchId,
    } as PropertyModelRequest;
  }

  public setUiModelFromRequestModel(request: any) {
    this.form.setValue({
      propertyDetails: {
        propertyType: request.propertyType,
        loanAmount: request.loanAmount,
        propertyValue: request.propertyValue,
        location: request.location,
        newBuild: request.newBuild,
        manualAddress: request.address,
      },
      propertySearchId: request.propertySearchId ?? '',
    } satisfies IUiPropertySearchModel);
  }

  public set auditResultsStatusChange(status: PropertyAuditResultsStatus) {
    this.auditResultsStatus = status;
  }

  public getPreviousOutcome(propertySearchId: string) {
    return this.dataService.get(`Property/outcome/${propertySearchId}`);
  }
}
